import { AfterContentInit, ChangeDetectorRef, Component, ContentChildren, EventEmitter, forwardRef, Inject, Input, OnDestroy, Output, QueryList, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { PrimeTemplate } from 'primeng/api';
import { Accordion } from 'primeng/accordion';
import { Subscription } from 'rxjs';

/*
  PrimeNG's original Accordion API (p-accordion with p-accordionTab children), kept as our own pair
  of components on top of PrimeNG's new accordion.

  AccordionTab was removed in PrimeNG 20. Its replacement keys panels by a value set on the
  accordion, but our pages - Make a Booking's flow above all - drive each tab with its own
  [(selected)], open and close tabs from code, and use the accordion's activeIndex.

  So these take the inputs, outputs and templates the old ones did and behave as they did:
  - [(selected)] on a tab, and opening a tab closes the others (emitting selectedChange(false)
    for each) unless the accordion is multiple
  - activeIndex / activeIndexChange / onOpen / onClose on the accordion, a tab's index being its
    position among the tabs actually rendered
  - a disabled tab's header is faded and cannot be clicked or keyed open; its content is not
    disabled, as before
  - a header template replaces the header text; any other template is the content, created when
    the tab is first opened and kept if cache is on
  - the toggle icon sits at the start of the header

  Each tab draws its own single-panel PrimeNG accordion: a PrimeNG panel has to sit inside a
  PrimeNG accordion in the same template, and a projected tab cannot reach one in ours. Our CSS
  already draws every panel as a separate rounded box, so it looks the same.
*/

@Component({
  selector: 'app-accordion',
  styleUrls: ['./host-block.css'],
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccordionComponent implements AfterContentInit, OnDestroy {
  @Input() multiple: boolean = false;
  @Input() expandIcon: string = 'pi pi-chevron-right';
  @Input() collapseIcon: string = 'pi pi-chevron-down';

  @Input() get activeIndex(): any { return this._activeIndex; }
  set activeIndex(value: any) {
    this._activeIndex = value;
    if (this.preventActiveIndexPropagation) {
      this.preventActiveIndexPropagation = false;
      return;
    }
    this.updateSelectionState();
  }
  @Output() activeIndexChange = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<{ originalEvent: Event | null, index: number }>();
  @Output() onClose = new EventEmitter<{ originalEvent: Event | null, index: number }>();

  @ContentChildren(forwardRef(() => AccordionTabComponent)) tabList: QueryList<AccordionTabComponent>;

  tabs: AccordionTabComponent[] = [];
  private _activeIndex: any;
  private preventActiveIndexPropagation = false;
  private tabsChanged: Subscription;

  ngAfterContentInit(): void {
    this.initTabs();
    this.tabsChanged = this.tabList.changes.subscribe(() => this.initTabs());
  }

  ngOnDestroy(): void {
    this.tabsChanged?.unsubscribe();
  }

  initTabs() {
    this.tabs = this.tabList.toArray();
    this.updateSelectionState();
  }

  indexOf(tab: AccordionTabComponent): number {
    return this.tabs.indexOf(tab);
  }

  //activeIndex set from outside opens the matching tab(s) and closes the rest
  updateSelectionState() {
    if (!this.tabs.length || this._activeIndex == null)
      return;
    this.tabs.forEach((tab, i) => {
      const selected = this.multiple ? this._activeIndex.includes(i) : i === this._activeIndex;
      if (selected !== tab.selected) {
        tab.selected = selected;
        if (selected)
          tab.loaded = true;
        tab.selectedChange.emit(selected);
        tab.cd.markForCheck();
      }
    });
  }

  //Opening a tab closes the others unless multiple
  closeOthers(except: AccordionTabComponent) {
    if (this.multiple)
      return;
    this.tabs.forEach(tab => {
      if (tab !== except && tab.selected) {
        tab.selected = false;
        tab.selectedChange.emit(false);
        tab.cd.markForCheck();
      }
    });
  }

  updateActiveIndex() {
    let index: any = this.multiple ? [] : null;
    this.tabs.forEach((tab, i) => {
      if (tab.selected) {
        if (this.multiple)
          index.push(i);
        else if (index === null)
          index = i;
      }
    });
    this.preventActiveIndexPropagation = true;
    this._activeIndex = index;
    this.activeIndexChange.emit(index);
  }
}

@Component({
  selector: 'app-accordiontab',
  styleUrls: ['./host-block.css'],
  template: `
    <p-accordion [value]="selected ? 0 : null" (valueChange)="onValueChange($event)">
      <p-accordion-panel [value]="0">
        <p-accordion-header [class.p-disabled]="disabled">
          <span [class]="(selected ? accordion.collapseIcon : accordion.expandIcon) + ' p-accordionheader-toggle-icon icon-start'" aria-hidden="true"></span>
          @if (headerTemplate) {
            <ng-container *ngTemplateOutlet="headerTemplate"></ng-container>
          } @else {
            {{ header }}
          }
          <!-- Empty, so PrimeNG does not add its own icon at the end -->
          <ng-template #toggleicon></ng-template>
        </p-accordion-header>
        <p-accordion-content>
          <ng-content></ng-content>
          @if (contentTemplate && (cache ? loaded : selected)) {
            <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
          }
        </p-accordion-content>
      </p-accordion-panel>
    </p-accordion>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccordionTabComponent implements AfterContentInit {
  @Input() header: string = '';
  @Input() disabled: boolean = false;
  @Input() cache: boolean = true;

  //Opened from the page's code as well as by a click, so a content template counts as loaded either way
  @Input() get selected(): boolean { return this._selected; }
  set selected(value: boolean) {
    this._selected = !!value;
    if (this._selected)
      this.loaded = true;
  }
  @Output() selectedChange = new EventEmitter<boolean>();
  private _selected: boolean = false;

  //Direct children only, as the old tab - a table's body template inside a tab is the table's
  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate>;
  @ViewChild(Accordion) private inner: Accordion;

  headerTemplate: TemplateRef<any> | null = null;
  contentTemplate: TemplateRef<any> | null = null;
  loaded: boolean = false;

  constructor(@Inject(forwardRef(() => AccordionComponent)) public accordion: AccordionComponent, public cd: ChangeDetectorRef) { }

  ngAfterContentInit(): void {
    this.templates.forEach(t => {
      if (t.getType() == 'header')
        this.headerTemplate = t.template;
      else
        this.contentTemplate = t.template;
    });
  }

  //The header was clicked or keyed: open is 0, closed is anything else
  onValueChange(value: any) {
    const open = value === 0;

    //A disabled header cannot be clicked, but Enter would still toggle it - put it back
    if (this.disabled) {
      this.inner?.value.set(this.selected ? 0 : null);
      return;
    }

    if (open === this.selected)
      return;

    const index = this.accordion.indexOf(this);
    if (open) {
      this.accordion.closeOthers(this);
      this.selected = true;
      this.loaded = true;
      this.accordion.onOpen.emit({ originalEvent: null, index });
    }
    else {
      this.selected = false;
      this.accordion.onClose.emit({ originalEvent: null, index });
    }
    this.selectedChange.emit(this.selected);
    this.accordion.updateActiveIndex();
    this.cd.markForCheck();
  }
}
